/**
 * Sección "Demos" del panel de Superadmin: invitaciones de prueba a tumunicipio
 * (la demo comercial) para prospectos. Se usa desde pages/Superadmin.jsx con el
 * token de superadmin.
 *
 *   GET  /api/superadmin/demos                 → la lista (+ max_dias, email_contacto)
 *   POST /api/superadmin/demos                 → invitar { nombre, email, rol, dias }
 *   POST /api/superadmin/demos/:id/reenviar    → link nuevo + mail (solo pendientes)
 *   PUT  /api/superadmin/demos/:id/extender    → +N días; reabre una vencida o revocada
 *   PUT  /api/superadmin/demos/:id/revocar     → corta el acceso de inmediato
 *
 * Cada invitado recibe una cuenta de Administrador u Operador de tumunicipio con
 * un plazo en días que corre desde que se lo invita. Los invitados demo no
 * cuentan para el tope de personal ni aparecen en el Personal del municipio:
 * se gestionan solo desde acá. El estado lo calcula el backend (pendiente,
 * activo, vencida, revocada) — acá solo se muestra.
 *
 * El rol NO tiene valor por defecto: hay que elegirlo en cada invitación (un
 * Administrador demo tiene mucho más poder que un Operador). El tope de días
 * (`max_dias`) viene del backend, no está escrito acá.
 *
 * Link de activación: se muestra en el resultado de invitar/reenviar (también,
 * y sobre todo, cuando el mail NO salió) y en cada tarjeta pendiente, con un
 * botón para copiarlo. Revocar pide confirmación en línea (no un diálogo
 * nativo): el invitado pierde el acceso de inmediato, sesión abierta incluida.
 *
 * Mismo estilo visual y mismos patrones que SuperadminClientes.jsx.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { STROKE_DEGRADE, IconExito, IconAdvertencia, IconEmail, IconPersona, IconPersonal, IconCopiar } from './icons';
import { formatearFecha } from '../utils/fecha';
import '../styles/login.css';

const API_URL = process.env.REACT_APP_API_URL;

const ESTADOS = {
  pendiente: { texto: 'Pendiente', fondo: 'rgba(243, 156, 18, 0.16)', color: '#9a6b00' },
  activo: { texto: 'Activo', fondo: 'rgba(0, 217, 163, 0.14)', color: '#0f8a6d' },
  vencida: { texto: 'Vencida', fondo: 'rgba(231, 76, 60, 0.12)', color: '#c0392b' },
  revocada: { texto: 'Revocada', fondo: '#eef0f2', color: '#5f6b73' },
};

const ROLES = { admin: 'Administrador', operador: 'Operador' };

// El backend manda los datetime como 'YYYY-MM-DD HH:MM:SS' (hora del servidor).
// Se muestran tal cual, sin new Date(): sin corrimiento por huso horario.
const formatearFechaHora = (valor) => {
  if (!valor) return '-';
  const [fecha, hora] = String(valor).split(' ');
  return hora ? `${formatearFecha(fecha)} ${hora.slice(0, 5)}` : formatearFecha(fecha);
};

const textoDias = (n) => (n === 1 ? '1 día' : `${n} días`);

const FORM_VACIO = { nombre: '', email: '', rol: '', dias: '7' };

// 1..max (entero) o NaN.
const parsearDias = (texto, max) => {
  const valor = String(texto ?? '').trim();
  if (valor === '') return NaN;
  const n = Number(valor);
  return Number.isInteger(n) && n >= 1 && n <= max ? n : NaN;
};

// Copia al portapapeles. navigator.clipboard solo existe en contextos seguros
// (https): el panel lo es, pero queda el respaldo clásico por si el navegador
// lo bloquea.
const copiarTexto = async (texto) => {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch (e) {
    try {
      const area = document.createElement('textarea');
      area.value = texto;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(area);
      return ok;
    } catch (e2) {
      return false;
    }
  }
};

const SuperadminDemos = ({ token, activa, onSesionExpirada }) => {
  const [demos, setDemos] = useState(null); // null = todavía no cargó
  const [maxDias, setMaxDias] = useState(30);
  const [emailContacto, setEmailContacto] = useState('');
  const [cargando, setCargando] = useState(false);
  const [errorLista, setErrorLista] = useState('');

  const [form, setForm] = useState(FORM_VACIO);
  const [enviando, setEnviando] = useState(false);
  // Resultado de la última invitación: { tipo: 'exito' | 'aviso' | 'error', texto, link }
  const [resultadoAlta, setResultadoAlta] = useState(null);

  // Resultado de la última acción de cada tarjeta: { [id]: { tipo, texto, link? } }
  const [mensajes, setMensajes] = useState({});
  const [reenviandoId, setReenviandoId] = useState(null);
  // Una tarjeta a la vez para cada formulario en línea. null = ninguno abierto.
  const [extendiendo, setExtendiendo] = useState(null); // { id, dias, guardando, error }
  const [revocando, setRevocando] = useState(null); // { id, guardando, error }
  // Link recién copiado (para el "Copiado" de 2 s): la clave es 'alta' o el id.
  const [copiado, setCopiado] = useState(null);
  const temporizadorCopiado = useRef(null);

  const cargaActual = useRef(0);
  // Superadmin.jsx pasa onSesionExpirada como función nueva en cada render: se
  // guarda en un ref para que no entre en las dependencias de cargar().
  const sesionExpiradaRef = useRef(onSesionExpirada);
  sesionExpiradaRef.current = onSesionExpirada;

  const headers = { Authorization: `Bearer ${token}` };

  const cargar = useCallback(async () => {
    const numero = ++cargaActual.current;
    setCargando(true);
    setErrorLista('');
    try {
      const { data } = await axios.get(`${API_URL}/superadmin/demos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (numero !== cargaActual.current) return;
      setDemos(data.demos || []);
      if (data.max_dias) setMaxDias(data.max_dias);
      setEmailContacto(data.email_contacto || '');
    } catch (err) {
      if (numero !== cargaActual.current) return;
      if (err.response?.status === 401) {
        sesionExpiradaRef.current();
        return;
      }
      console.error('Error al listar las demos:', err);
      setErrorLista(err.response?.data?.mensaje || 'No se pudo cargar la lista de demos. Probá de nuevo.');
    } finally {
      if (numero === cargaActual.current) setCargando(false);
    }
  }, [token]);

  // La sección queda montada aunque esté oculta (ver Superadmin.jsx): se
  // recarga cada vez que se vuelve a esta pestaña, porque el estado cambia solo
  // (alguien activa su cuenta, entra, o se vence el plazo).
  useEffect(() => {
    if (activa) cargar();
  }, [activa, cargar]);

  useEffect(() => () => clearTimeout(temporizadorCopiado.current), []);

  const copiar = async (clave, link) => {
    const ok = await copiarTexto(link);
    setCopiado(ok ? clave : `error-${clave}`);
    clearTimeout(temporizadorCopiado.current);
    temporizadorCopiado.current = setTimeout(() => setCopiado(null), 2500);
  };

  const dias = parsearDias(form.dias, maxDias);
  const formValido = form.nombre.trim() !== '' && form.email.trim() !== '' && form.rol !== '' && !Number.isNaN(dias);

  const invitar = async (e) => {
    e.preventDefault();
    if (!formValido || enviando) return;
    setEnviando(true);
    setResultadoAlta(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/superadmin/demos`,
        { nombre: form.nombre.trim(), email: form.email.trim(), rol: form.rol, dias },
        { headers }
      );
      setResultadoAlta({ tipo: data.mail_enviado ? 'exito' : 'aviso', texto: data.mensaje, link: data.link_activacion });
      setForm(FORM_VACIO);
      await cargar();
    } catch (err) {
      if (err.response?.status === 401) {
        sesionExpiradaRef.current();
        return;
      }
      console.error('Error al invitar:', err);
      setResultadoAlta({ tipo: 'error', texto: err.response?.data?.mensaje || 'No se pudo enviar la invitación. Probá de nuevo.' });
    } finally {
      setEnviando(false);
    }
  };

  const ponerMensaje = (id, mensaje) => setMensajes((prev) => ({ ...prev, [id]: mensaje }));

  const reenviar = async (demo) => {
    setReenviandoId(demo.id);
    ponerMensaje(demo.id, undefined);
    try {
      const { data } = await axios.post(`${API_URL}/superadmin/demos/${demo.id}/reenviar`, {}, { headers });
      ponerMensaje(demo.id, { tipo: data.mail_enviado ? 'exito' : 'aviso', texto: data.mensaje, link: data.link_activacion });
      await cargar();
    } catch (err) {
      if (err.response?.status === 401) {
        sesionExpiradaRef.current();
        return;
      }
      console.error('Error al reenviar la invitación demo:', err);
      ponerMensaje(demo.id, { tipo: 'error', texto: err.response?.data?.mensaje || 'No se pudo reenviar. Probá de nuevo.' });
      // Si el estado cambió por otro lado (ej. ya había activado), se resincroniza.
      if (err.response?.status === 409) await cargar();
    } finally {
      setReenviandoId(null);
    }
  };

  const abrirExtender = (demo) => {
    ponerMensaje(demo.id, undefined);
    setRevocando(null);
    setExtendiendo({ id: demo.id, dias: '7', guardando: false, error: '' });
  };

  const extender = async (demo) => {
    const n = parsearDias(extendiendo.dias, maxDias);
    if (Number.isNaN(n)) return;
    setExtendiendo((prev) => ({ ...prev, guardando: true, error: '' }));
    try {
      const { data } = await axios.put(`${API_URL}/superadmin/demos/${demo.id}/extender`, { dias: n }, { headers });
      setExtendiendo(null);
      ponerMensaje(demo.id, { tipo: 'exito', texto: data.mensaje });
      await cargar();
    } catch (err) {
      if (err.response?.status === 401) {
        sesionExpiradaRef.current();
        return;
      }
      console.error('Error al extender la demo:', err);
      setExtendiendo((prev) => ({ ...prev, guardando: false, error: err.response?.data?.mensaje || 'No se pudo extender. Probá de nuevo.' }));
    }
  };

  const abrirRevocar = (demo) => {
    ponerMensaje(demo.id, undefined);
    setExtendiendo(null);
    setRevocando({ id: demo.id, guardando: false, error: '' });
  };

  const revocar = async (demo) => {
    setRevocando((prev) => ({ ...prev, guardando: true, error: '' }));
    try {
      const { data } = await axios.put(`${API_URL}/superadmin/demos/${demo.id}/revocar`, {}, { headers });
      setRevocando(null);
      ponerMensaje(demo.id, { tipo: 'exito', texto: data.mensaje });
      await cargar();
    } catch (err) {
      if (err.response?.status === 401) {
        sesionExpiradaRef.current();
        return;
      }
      console.error('Error al revocar la demo:', err);
      setRevocando((prev) => ({ ...prev, guardando: false, error: err.response?.data?.mensaje || 'No se pudo revocar. Probá de nuevo.' }));
    }
  };

  // Caja con el link de activación + botón de copiar. `clave` identifica cuál
  // se copió ('alta' o el id de la demo).
  const cajaLink = (clave, link) => (
    <div style={estilos.cajaLink}>
      <input
        type="text"
        readOnly
        value={link}
        onFocus={(e) => e.target.select()}
        aria-label="Link de activación"
        style={estilos.inputLink}
      />
      <button type="button" onClick={() => copiar(clave, link)} style={estilos.botonCopiar}>
        <IconCopiar size={14} stroke="currentColor" />
        {copiado === clave ? 'Copiado' : 'Copiar link'}
      </button>
      {copiado === `error-${clave}` && (
        <span style={estilos.errorCopiar}>No se pudo copiar solo: seleccioná el link y copialo a mano.</span>
      )}
    </div>
  );

  const cuerpoMensaje = (m, clave) => (
    <div style={cajaMensaje(m.tipo)} role={m.tipo === 'error' ? 'alert' : 'status'}>
      <span style={{ flexShrink: 0, marginTop: 1 }}>
        {m.tipo === 'exito' ? <IconExito size={16} stroke="currentColor" /> : <IconAdvertencia size={16} stroke="currentColor" />}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div>{m.texto}</div>
        {m.link && (
          <>
            {m.tipo === 'aviso' && (
              <p style={{ margin: '8px 0 6px', fontWeight: 600 }}>
                Mandale este link a mano (WhatsApp, por ejemplo): es lo que necesita para activar su cuenta.
              </p>
            )}
            {cajaLink(clave, m.link)}
          </>
        )}
      </div>
    </div>
  );

  return (
    <div style={estilos.contenedor}>
      <form onSubmit={invitar} style={estilos.formAlta}>
        <p style={subtituloSeccion}>Invitar a probar tumunicipio</p>
        <p style={estilos.ayuda}>
          Recibe un mail con un link para activar su cuenta. El acceso dura los días que elijas, contados desde ahora, y
          no ocupa lugar en el tope de personal. En su primer ingreso va a necesitar una app autenticadora (Google Authenticator).
        </p>

        <div className="form-group">
          <label htmlFor="demoNombre">Nombre</label>
          <div className="input-wrapper">
            <span className="input-icon"><IconPersona size={16} stroke={STROKE_DEGRADE} /></span>
            <input
              id="demoNombre"
              type="text"
              placeholder="Nombre y apellido"
              value={form.nombre}
              onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
              className="form-input"
              disabled={enviando}
              maxLength={255}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="demoEmail">Email</label>
          <div className="input-wrapper">
            <span className="input-icon"><IconEmail size={16} stroke={STROKE_DEGRADE} /></span>
            <input
              id="demoEmail"
              type="email"
              placeholder="nombre@municipio.gob.ar"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              className="form-input"
              disabled={enviando}
              required
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="demoRol">Rol</label>
          <div className="input-wrapper">
            <span className="input-icon"><IconPersonal size={16} stroke={STROKE_DEGRADE} /></span>
            <select
              id="demoRol"
              value={form.rol}
              onChange={(e) => setForm((p) => ({ ...p, rol: e.target.value }))}
              className="form-input"
              disabled={enviando}
              required
            >
              <option value="" disabled>Elegí un rol…</option>
              <option value="operador">Operador</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          <span style={estilos.ayuda}>
            Un Administrador ve y modifica mucho más (Diseñador, Reportes, Auditoría). Un Operador solo usa el Padrón.
          </span>
        </div>

        <div className="form-group">
          <label htmlFor="demoDias">Días de acceso</label>
          <div className="input-wrapper">
            <span className="input-icon"><IconExito size={16} stroke={STROKE_DEGRADE} /></span>
            <input
              id="demoDias"
              type="number"
              min="1"
              max={maxDias}
              step="1"
              inputMode="numeric"
              value={form.dias}
              onChange={(e) => setForm((p) => ({ ...p, dias: e.target.value }))}
              className="form-input"
              disabled={enviando}
              required
            />
          </div>
          <span style={estilos.ayuda}>De 1 a {maxDias} días. Después se puede extender.</span>
        </div>

        <button type="submit" className="btn-ingresar" disabled={!formValido || enviando}>
          {enviando ? 'Enviando invitación...' : 'Invitar'}
        </button>

        {resultadoAlta && cuerpoMensaje(resultadoAlta, 'alta')}
      </form>

      <div style={estilos.encabezado}>
        <p style={subtituloSeccion}>
          {demos === null ? 'Invitaciones' : demos.length === 1 ? '1 invitación' : `${demos.length} invitaciones`}
        </p>
        <button type="button" className="link-ayuda" onClick={cargar} disabled={cargando} style={linkBoton}>
          {cargando ? 'Actualizando...' : 'Actualizar'}
        </button>
      </div>

      {errorLista && (
        <div style={cajaMensaje('error')} role="alert">
          <span style={{ flexShrink: 0, marginTop: 1 }}><IconAdvertencia size={16} stroke="currentColor" /></span>
          <div>{errorLista}</div>
        </div>
      )}

      {demos === null && !errorLista && <p style={estilos.estadoVacio}>Cargando invitaciones...</p>}
      {demos !== null && demos.length === 0 && <p style={estilos.estadoVacio}>Todavía no invitaste a nadie.</p>}

      {demos !== null && (
        <ul style={estilos.lista}>
          {demos.map((d) => {
            const estado = ESTADOS[d.estado] || ESTADOS.revocada;
            const mensaje = mensajes[d.id];
            const reabre = d.estado === 'vencida' || d.estado === 'revocada';
            const dias2 = extendiendo?.id === d.id ? parsearDias(extendiendo.dias, maxDias) : NaN;
            return (
              <li key={d.id} style={estilos.tarjeta} data-estado={d.estado}>
                <div style={estilos.filaSuperior}>
                  <div style={estilos.bloqueNombre}>
                    <p style={estilos.nombre}>{d.nombre}</p>
                    <p style={estilos.email}>{d.email}</p>
                  </div>
                  <span style={estilos.badge(estado)}>{estado.texto}</span>
                </div>

                <dl style={estilos.datos}>
                  <div style={estilos.dato}>
                    <dt style={estilos.datoTitulo}>Rol</dt>
                    <dd style={estilos.datoValor}>{ROLES[d.rol] || d.rol}</dd>
                  </div>
                  <div style={estilos.dato}>
                    <dt style={estilos.datoTitulo}>{d.estado === 'vencida' ? 'Venció' : 'Vence'}</dt>
                    <dd style={estilos.datoValor}>
                      {formatearFechaHora(d.demo_vence)}
                      {(d.estado === 'activo' || d.estado === 'pendiente') && ` (${d.dias_restantes === 0 ? 'hoy' : `faltan ${textoDias(d.dias_restantes)}`})`}
                    </dd>
                  </div>
                  <div style={estilos.dato}>
                    <dt style={estilos.datoTitulo}>Entró</dt>
                    <dd style={d.ultimo_acceso ? estilos.datoValor : estilos.datoValorApagado}>
                      {d.ultimo_acceso ? formatearFechaHora(d.ultimo_acceso) : 'Todavía no entró'}
                    </dd>
                  </div>
                  <div style={estilos.dato}>
                    <dt style={estilos.datoTitulo}>Invitada</dt>
                    <dd style={estilos.datoValor}>{formatearFechaHora(d.demo_creada_en)}</dd>
                  </div>
                </dl>

                {d.estado === 'pendiente' && d.link_activacion && (
                  <div>
                    <p style={estilos.ayuda}>Link de activación (todavía no lo usó):</p>
                    {cajaLink(d.id, d.link_activacion)}
                  </div>
                )}

                {extendiendo?.id === d.id ? (
                  <form
                    style={estilos.formEnLinea}
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!extendiendo.guardando && !Number.isNaN(dias2)) extender(d);
                    }}
                  >
                    <label htmlFor={`extender-${d.id}`} style={estilos.labelEnLinea}>
                      {reabre ? 'Reabrir por cuántos días' : 'Sumar cuántos días'}
                    </label>
                    <input
                      id={`extender-${d.id}`}
                      type="number"
                      min="1"
                      max={maxDias}
                      step="1"
                      inputMode="numeric"
                      autoFocus
                      value={extendiendo.dias}
                      onChange={(e) => setExtendiendo((p) => ({ ...p, dias: e.target.value, error: '' }))}
                      className="form-input"
                      style={{ padding: '10px 12px' }}
                      disabled={extendiendo.guardando}
                    />
                    <p style={estilos.ayuda}>
                      De 1 a {maxDias}. {reabre ? 'Se cuentan desde ahora.' : 'Se suman al vencimiento actual.'}
                    </p>
                    {extendiendo.error && (
                      <div style={cajaMensaje('error')} role="alert">
                        <span style={{ flexShrink: 0, marginTop: 1 }}><IconAdvertencia size={16} stroke="currentColor" /></span>
                        <div>{extendiendo.error}</div>
                      </div>
                    )}
                    <div style={estilos.filaBotones}>
                      <button type="submit" style={estilos.botonPrimario(extendiendo.guardando || Number.isNaN(dias2))} disabled={extendiendo.guardando || Number.isNaN(dias2)}>
                        {extendiendo.guardando ? 'Guardando...' : reabre ? 'Reabrir' : 'Extender'}
                      </button>
                      <button type="button" style={estilos.botonSecundario} disabled={extendiendo.guardando} onClick={() => setExtendiendo(null)}>
                        Cancelar
                      </button>
                    </div>
                  </form>
                ) : revocando?.id === d.id ? (
                  <div style={cajaMensaje('aviso')} role="alert">
                    <span style={{ flexShrink: 0, marginTop: 1 }}><IconAdvertencia size={16} stroke="currentColor" /></span>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: '0 0 10px' }}>
                        {d.nombre} pierde el acceso de inmediato, sesión abierta incluida: sale en su próxima acción.
                        Se puede reabrir después con "Reabrir".
                      </p>
                      {revocando.error && <p style={{ margin: '0 0 10px', color: '#c0392b', fontWeight: 600 }}>{revocando.error}</p>}
                      <div style={estilos.filaBotones}>
                        <button type="button" style={estilos.botonPeligro(revocando.guardando)} disabled={revocando.guardando} onClick={() => revocar(d)}>
                          {revocando.guardando ? 'Revocando...' : 'Sí, revocar'}
                        </button>
                        <button type="button" style={estilos.botonSecundario} disabled={revocando.guardando} onClick={() => setRevocando(null)}>
                          No
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={estilos.filaBotones}>
                    {d.estado === 'pendiente' && (
                      <button type="button" style={estilos.botonPrimario(reenviandoId !== null)} disabled={reenviandoId !== null} onClick={() => reenviar(d)}>
                        <IconEmail size={14} stroke="currentColor" />
                        {reenviandoId === d.id ? 'Enviando...' : 'Reenviar'}
                      </button>
                    )}
                    <button type="button" style={estilos.botonSecundario} onClick={() => abrirExtender(d)}>
                      {reabre ? 'Reabrir' : 'Extender'}
                    </button>
                    {d.estado !== 'revocada' && (
                      <button type="button" style={estilos.botonQuitar} onClick={() => abrirRevocar(d)}>
                        Revocar
                      </button>
                    )}
                  </div>
                )}

                {mensaje && cuerpoMensaje(mensaje, d.id)}
              </li>
            );
          })}
        </ul>
      )}

      {emailContacto && (
        <p style={estilos.ayuda}>Los invitados escriben a {emailContacto} si tienen dudas o quieren extender su acceso.</p>
      )}
    </div>
  );
};

// --- estilos puntuales de esta sección (el resto sale de login.css) ---
const subtituloSeccion = {
  margin: 0,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: '#7f8c8d',
  fontFamily: 'var(--font-header)',
};

const linkBoton = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 13,
  padding: 4,
};

const estilos = {
  contenedor: { display: 'flex', flexDirection: 'column', gap: 16 },
  formAlta: { display: 'flex', flexDirection: 'column', gap: 14 },
  ayuda: { margin: 0, fontSize: 12, color: '#7f8c8d', lineHeight: 1.45 },
  encabezado: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 8 },
  estadoVacio: { margin: 0, padding: '24px 0', textAlign: 'center', fontSize: 13, color: '#7f8c8d' },
  lista: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 12 },
  tarjeta: {
    display: 'flex', flexDirection: 'column', gap: 12, padding: 16,
    background: '#f8f9fa', border: '2px solid #e0e0e0', borderRadius: 12,
  },
  // flexWrap: en pantallas angostas el badge (que no se achica) baja debajo del
  // nombre en vez de robarle el ancho y cortarlo.
  filaSuperior: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 },
  bloqueNombre: { flex: '1 1 150px', minWidth: 0 },
  nombre: {
    margin: 0, fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-header)',
    color: '#2c3e50', overflowWrap: 'break-word',
  },
  email: { margin: '2px 0 0', fontSize: 12, color: '#7f8c8d', overflowWrap: 'anywhere' },
  badge: (estado) => ({
    flexShrink: 0, padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
    fontFamily: 'var(--font-header)', letterSpacing: '0.3px', textTransform: 'uppercase',
    whiteSpace: 'nowrap', background: estado.fondo, color: estado.color,
  }),
  datos: { margin: 0, display: 'flex', flexDirection: 'column', gap: 4 },
  dato: { display: 'flex', gap: 8, fontSize: 12, lineHeight: 1.4 },
  datoTitulo: { flex: '0 0 58px', color: '#7f8c8d' },
  datoValor: { margin: 0, minWidth: 0, color: '#2c3e50', overflowWrap: 'anywhere' },
  datoValorApagado: { margin: 0, minWidth: 0, color: '#95a5a6', overflowWrap: 'anywhere' },
  cajaLink: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 6 },
  // minWidth 0 + flex 1 1 180px: el input del link se achica en 320 px y el
  // botón de copiar baja debajo en vez de desbordar.
  inputLink: {
    flex: '1 1 180px', minWidth: 0, boxSizing: 'border-box', padding: '9px 10px', fontSize: 12,
    border: '2px solid #e0e0e0', borderRadius: 10, background: '#ffffff', color: '#2c3e50',
    fontFamily: 'var(--font-body)',
  },
  botonCopiar: {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 12px', background: '#ffffff',
    color: 'var(--color-turquesa)', border: '2px solid var(--color-turquesa)', borderRadius: 10,
    fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-header)', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  errorCopiar: { flexBasis: '100%', fontSize: 12, color: '#c0392b' },
  formEnLinea: {
    display: 'flex', flexDirection: 'column', gap: 10, padding: 14,
    background: '#ffffff', border: '2px solid #e0e0e0', borderRadius: 12,
  },
  labelEnLinea: { fontSize: 13, fontWeight: 600, color: '#2c3e50', fontFamily: 'var(--font-header)' },
  filaBotones: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  botonPrimario: (deshabilitado) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px 16px',
    background: '#ffffff', color: 'var(--color-turquesa)', border: '2px solid var(--color-turquesa)',
    borderRadius: 10, fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-header)',
    cursor: deshabilitado ? 'not-allowed' : 'pointer', opacity: deshabilitado ? 0.6 : 1,
    transition: 'var(--transition)',
  }),
  botonSecundario: {
    padding: '10px 14px', background: 'transparent', color: '#5f6b73', border: '2px solid #dfe4e8',
    borderRadius: 10, fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-header)', cursor: 'pointer',
  },
  botonQuitar: {
    marginLeft: 'auto', padding: '10px 6px', background: 'none', border: 'none', color: '#c0392b',
    fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-header)', cursor: 'pointer',
  },
  botonPeligro: (deshabilitado) => ({
    padding: '10px 14px', background: '#c0392b', color: '#ffffff', border: 'none', borderRadius: 10,
    fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-header)',
    cursor: deshabilitado ? 'not-allowed' : 'pointer', opacity: deshabilitado ? 0.6 : 1,
  }),
};

const cajaMensaje = (tipo) => {
  const colores = {
    exito: { fondo: 'rgba(0, 217, 163, 0.1)', color: '#0f8a6d', borde: '#00d9a3' },
    aviso: { fondo: 'rgba(243, 156, 18, 0.12)', color: '#9a6b00', borde: '#f39c12' },
    error: { fondo: 'rgba(231, 76, 60, 0.1)', color: '#e74c3c', borde: '#e74c3c' },
  };
  const c = colores[tipo] || colores.aviso;
  return {
    display: 'flex', gap: 10, alignItems: 'flex-start', background: c.fondo, color: c.color,
    padding: '12px 14px', borderRadius: 12, fontSize: 13, borderLeft: `4px solid ${c.borde}`,
    lineHeight: 1.45, overflowWrap: 'anywhere',
  };
};

export default SuperadminDemos;
